/**
 * Day-mode race plan: which races each boat of a day should end up with.
 *
 * The Events wizard's step 4 list is the user's authoritative statement of a day's races. This
 * turns that list into a per-dataset decision — keep, remove, or "cannot place" — so the caller can
 * reconcile `dataset_events` to it (delete the stale RACE/PRESTART/LEG, then upsert the rest)
 * instead of patching whatever happened to be stored.
 *
 * Patching was the bug: the wizard used to build each boat's race payload from that boat's STORED
 * races and only append step-4 races whose number was new, so removing a row was a no-op, and a
 * re-add after "remove all" (numbered from 1 again, while the stored rows carry post-processing
 * YYMMDDii numbers) inserted a second copy of every race.
 */

export interface DayRacePlanDataset {
	dataset_id: number;
	/** Trimmed source name — must match the key the pill selection uses. */
	source_name: string;
	/** This dataset's own data window (ms epoch); a race outside it cannot be placed on this boat. */
	startMs: number;
	endMs: number;
	/** Race numbers currently stored in `dataset_events` for this dataset. */
	storedRaceNumbers: number[];
}

export interface DayRaceInput {
	raceNumber: number;
	startMs: number;
}

export interface DayRacePlanEntry {
	dataset_id: number;
	source_name: string;
	/** Races this dataset should have after the sync — the `keep_race_numbers` reconcile set. */
	keepRaceNumbers: number[];
	/** Stored races this dataset loses (deleted with their PRESTART/LEG and race_stats). */
	removedRaceNumbers: number[];
	/** Races wanted here but outside this dataset's window, so nothing can be written. */
	skippedOutOfWindow: number[];
}

export interface BuildDayRacePlanOptions {
	/** Step 4's race list (one entry per Race_number). Empty means "this day has no races". */
	races: DayRaceInput[];
	datasets: DayRacePlanDataset[];
	/**
	 * Whether races were added, removed or re-timed in this session. False makes the plan a no-op
	 * (keep exactly what is stored): a wizard visit that never touched races must not prune, and
	 * must not fan a race out to boats that do not have it.
	 */
	racesEdited: boolean;
	/**
	 * Whether this race has a source selection at all — seeded from the stored events, created by
	 * `addRace`, or clicked. False means "no opinion recorded", NOT "no boats": the stored
	 * membership is kept, so a save can never wipe a race off the fleet because its pills had not
	 * loaded yet.
	 */
	hasSelection: (raceNumber: number) => boolean;
	isSelected: (raceNumber: number, sourceName: string) => boolean;
}

const sortNumbers = (values: Iterable<number>): number[] => Array.from(values).sort((a, b) => a - b);

/**
 * Decide each dataset's race set. **What the source pills show is what gets saved.**
 *
 * There used to be a second rule behind the pills: until the user clicked one, membership came from
 * "the boat already has this race OR its data window covers the start". On a fleet day every boat's
 * window covers every race, so that put every boat in every race — while the pills on screen said
 * something else entirely. A selection that is displayed must be the selection that is written, so
 * the pills are now the only answer.
 *
 * A race with no selection recorded at all is not "no boats": its stored membership is kept, so a
 * save cannot wipe a race off the fleet because its pills had not been seeded yet.
 *
 * A selected race still needs a window that can hold it; those are reported rather than written as
 * events the boat has no data for.
 */
export function buildDayRacePlan(opts: BuildDayRacePlanOptions): DayRacePlanEntry[] {
	const { races, datasets, racesEdited, hasSelection, isSelected } = opts;
	const validRaces = races.filter((r) => Number.isFinite(r.raceNumber) && r.raceNumber > 0);

	return datasets.map((ds) => {
		const stored = new Set(ds.storedRaceNumbers.filter((rn) => rn > 0));
		const keep = new Set<number>();
		const skipped = new Set<number>();

		for (const race of validRaces) {
			const rn = race.raceNumber;
			const inWindow = Number.isFinite(race.startMs)
				&& Number.isFinite(ds.startMs)
				&& Number.isFinite(ds.endMs)
				&& race.startMs >= ds.startMs
				&& race.startMs < ds.endMs;

			const wanted = (racesEdited && hasSelection(rn))
				? isSelected(rn, ds.source_name)
				: stored.has(rn);
			if (!wanted) {
				continue;
			}

			// A race the boat does not already carry needs a window that covers its start, or there
			// is no telemetry to label and the next reconcile would drop it again.
			if (!stored.has(rn) && !inWindow) {
				skipped.add(rn);
				continue;
			}
			keep.add(rn);
		}

		const removed = racesEdited
			? sortNumbers(Array.from(stored).filter((rn) => !keep.has(rn)))
			: [];

		return {
			dataset_id: ds.dataset_id,
			source_name: ds.source_name,
			keepRaceNumbers: sortNumbers(keep),
			removedRaceNumbers: removed,
			skippedOutOfWindow: sortNumbers(skipped),
		};
	});
}

/** Distinct races the plan deletes, and how many boats lose at least one. */
export function summarizeRaceRemovals(plan: DayRacePlanEntry[]): {
	raceNumbers: number[];
	datasetCount: number;
} {
	const raceNumbers = new Set<number>();
	let datasetCount = 0;
	for (const entry of plan) {
		if (entry.removedRaceNumbers.length === 0) {
			continue;
		}
		datasetCount += 1;
		for (const rn of entry.removedRaceNumbers) {
			raceNumbers.add(rn);
		}
	}
	return { raceNumbers: sortNumbers(raceNumbers), datasetCount };
}

/** Step-4 races the plan places on no boat at all (a save would leave them nowhere). */
export function racesWithNoDataset(plan: DayRacePlanEntry[], races: DayRaceInput[]): number[] {
	const placed = new Set<number>();
	for (const entry of plan) {
		for (const rn of entry.keepRaceNumbers) {
			placed.add(rn);
		}
	}
	return sortNumbers(
		races
			.map((r) => r.raceNumber)
			.filter((rn) => rn > 0 && !placed.has(rn)),
	);
}

/** Highest 1-based race index a `YYMMDDii` label can carry (matches 2_processing's encoder). */
export const RACE_NUMBER_MAX_INDEX = 99;

/**
 * Encode a local calendar day plus a 1-based index as `YYMMDDii`.
 *
 * Mirrors `_race_number_yymmddi` in 2_processing.py: `('260920', 1)` → `26092001`.
 */
export function raceNumberForDayIndex(yymmdd: string, index: number): number {
	if (!/^\d{6}$/.test(yymmdd) || index < 1 || index > RACE_NUMBER_MAX_INDEX) {
		return 0;
	}
	return Number(`${yymmdd}${String(index).padStart(2, '0')}`);
}

/**
 * The `YYMMDDii` number a race added on `yymmdd` should get: the lowest index that day does not
 * already use. Returns 0 when the day is full (99 races) or `yymmdd` is unusable.
 *
 * A new race used to be numbered `max(existing) + 1`, which handed out 1, 2, 3, 4 on a day whose
 * races had been cleared — and those generic numbers stick, because a hand-entered race is never
 * renumbered by the pipeline (its number is the answer). Worse, on a day that still held good
 * YYMMDDii races the same rule produced `26092003 + 1`. Minting the real label here is the only
 * place a wizard-entered race can get one.
 *
 * Legacy numbers in 1..99 reserve their own index, so a day that already carries 1, 2, 3 continues
 * at index 4 rather than colliding with them once they are relabelled.
 */
export function nextRaceNumberForDay(existing: number[], yymmdd: string): number {
	if (!/^\d{6}$/.test(yymmdd)) {
		return 0;
	}
	const dayPrefix = Number(yymmdd);
	const used = new Set<number>();
	for (const rn of existing) {
		if (!Number.isFinite(rn) || rn <= 0) {
			continue;
		}
		if (Math.floor(rn / 100) === dayPrefix) {
			used.add(rn % 100);
		} else if (rn <= RACE_NUMBER_MAX_INDEX) {
			used.add(rn);
		}
	}
	for (let index = 1; index <= RACE_NUMBER_MAX_INDEX; index += 1) {
		if (!used.has(index)) {
			return raceNumberForDayIndex(yymmdd, index);
		}
	}
	return 0;
}

/** (source, races) pairs the plan wanted but could not place inside the boat's data window. */
export function racesOutsideDatasetWindow(plan: DayRacePlanEntry[]): { source_name: string; raceNumbers: number[] }[] {
	return plan
		.filter((entry) => entry.skippedOutOfWindow.length > 0)
		.map((entry) => ({ source_name: entry.source_name, raceNumbers: entry.skippedOutOfWindow }));
}
